import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { StartupProfileData } from '../../api/startup-profile.api';
import type { StartupProfileFormValues } from './schema';
import type { StartupSectionId } from './startup-sections';
import { LogoSection } from './LogoSection';
import { CompanyOverviewSection } from './CompanyOverviewSection';
import { FinancialsSection } from './FinancialsSection';
import { CurrentRaiseSection } from './CurrentRaiseSection';
import { PitchMaterialsSection } from './PitchMaterialsSection';
import { FoundingTeamSection } from './FoundingTeamSection';
import {
  CompanyDetailsCard,
  CurrentRaiseCard,
  FinancialsCard,
  type StartupCardProps,
} from './StartupDetailCards';
import { FoundingTeamCard, PitchMaterialsCard } from './StartupTeamPitchCards';

interface EditProps {
  data: StartupProfileData;
  form: UseFormReturn<StartupProfileFormValues>;
  /** Upload tracker key -> callback, from `useUploadsInFlight().track`. */
  trackUpload: (id: string) => (isUploading: boolean) => void;
}

/** Each card's read view, laid out as in the client-approved mockup (FR-172). */
export const STARTUP_SECTION_VIEWS: Record<
  StartupSectionId,
  (props: StartupCardProps) => ReactNode
> = {
  company: (p) => <CompanyDetailsCard {...p} />,
  raise: (p) => <CurrentRaiseCard {...p} />,
  financials: (p) => <FinancialsCard {...p} />,
  team: (p) => <FoundingTeamCard {...p} />,
  pitch: (p) => <PitchMaterialsCard {...p} />,
};

/**
 * Each side panel's fields: the same section component in `edit` mode, bound
 * to the panel's own form. Company details carries the logo upload (FR-172).
 */
export const STARTUP_SECTION_EDITORS: Record<StartupSectionId, (props: EditProps) => ReactNode> = {
  company: ({ data, form, trackUpload }) => (
    <div className="flex flex-col gap-8">
      <LogoSection
        mode="edit"
        data={data}
        watch={form.watch}
        setValue={form.setValue}
        errors={form.formState.errors}
        onUploadingChange={trackUpload('logo')}
      />
      <CompanyOverviewSection
        mode="edit"
        data={data}
        control={form.control}
        register={form.register}
        setValue={form.setValue}
        watch={form.watch}
        errors={form.formState.errors}
      />
    </div>
  ),
  financials: ({ data, form }) => (
    <FinancialsSection
      mode="edit"
      data={data}
      control={form.control}
      register={form.register}
      errors={form.formState.errors}
    />
  ),
  raise: ({ data, form }) => (
    <CurrentRaiseSection
      mode="edit"
      data={data}
      control={form.control}
      register={form.register}
      errors={form.formState.errors}
    />
  ),
  pitch: ({ data, form, trackUpload }) => (
    <PitchMaterialsSection
      mode="edit"
      data={data}
      register={form.register}
      watch={form.watch}
      setValue={form.setValue}
      errors={form.formState.errors}
      onUploadingChange={trackUpload('pitchDeck')}
    />
  ),
  team: ({ data, form }) => (
    <FoundingTeamSection
      mode="edit"
      data={data}
      setValue={form.setValue}
      watch={form.watch}
      errors={form.formState.errors}
      isSubmitted={form.formState.isSubmitted}
    />
  ),
};
